import { memo, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiActions,
  MiAvatar,
  MiButton,
  MiIcon,
  MiInputTextbox,
  MiInternalDrawer,
  MiSectionHeader,
} from "@miplatform/components";
import {
  ACTIONS_LIST_TYPE,
  BOARD_MODAL_MODE,
  BUTTON_VARIANTS,
  ICONS_WEB,
  PERMISSIONS,
  SECTION_HEADER_VARIANTS,
  TECHNICIAN_ACTIVITY_STATUSES_KEYED,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import {
  addAlpha,
  deepEqual,
  getInitials,
  getRootUrl,
  isEmpty,
  toKebabCase,
} from "@miplatform/utils";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, InputAdornment, Typography } from "@mui/material";

const SearchTechnician = ({ search, setSearch }) => {
  const { classes } = useStyles();

  return (
    <MiInputTextbox
      className={classes.searchContainer}
      placeholder="Search Technicians"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      textfieldProps={{
        startAdornment: (
          <InputAdornment position="start" style={{ margin: 0 }}>
            <MiIcon path={mdi[ICONS_WEB.MAGNIFY]} />
          </InputAdornment>
        ),
        endAdornment: search && (
          <InputAdornment position="end" style={{ margin: 0 }}>
            <MiButton
              startIcon={{ path: mdi[ICONS_WEB.CLOSE] }}
              className={classes.iconButton}
              onClick={() => setSearch("")}
              data-testid="clear-search-btn"
            />
          </InputAdornment>
        ),
        className: classes.searchInput,
        "data-testid": "technician-search-input",
      }}
    />
  );
};

const UserCard = ({
  user,
  onAction,
  isOnBoard,
  displayOrder,
  readOnly,
  isDraggable,
  isSelected,
  hideAction,
  onSelect = () => {},
}) => {
  const { name, activityStatus } = user;
  const activityStatusObj = TECHNICIAN_ACTIVITY_STATUSES_KEYED[activityStatus];

  const {
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
    setNodeRef,
  } = useSortable({
    id: user.id,
  });

  const { classes, theme } = useStyles({
    transform,
    transition,
    isDragging,
    isSelected,
    isOnBoard,
  });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      className={classes.userCardContainer}
      onClick={onSelect}
      data-testid={`user-card-${toKebabCase(name)}`}
    >
      <Box className={classes.userCardNameContainer}>
        {isDraggable && !readOnly && !hideAction && (
          <MiButton
            className={classes.iconButton}
            startIcon={{
              path: mdi[ICONS_WEB.DRAG],
              iconSize: 0.8,
              iconColor: theme.palette.grey.medium,
            }}
            {...listeners}
            style={{ cursor: "grab", padding: theme.spacing(1, 0) }}
            data-testid="drag-handle-btn"
          />
        )}
        <MiAvatar
          className={classes.avatar}
          data-testid="user-initials"
          src={
            user?.profilePicture
              ? `${getRootUrl()}/document-service/documents/${
                  user?.profilePicture.id
                }`
              : undefined
          }
        >
          {getInitials(name)}
        </MiAvatar>
        <Box className={classes.userCardContentContainer}>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.BODY2}
            fontWeight={theme.fontWeight.bold}
          >{`${displayOrder ? `${displayOrder}. ` : ""}${name}`}</Typography>
          <Box display="flex" flexDirection="row">
            <Box
              className={classes.statusContainer}
              backgroundColor={addAlpha(activityStatusObj.color, 25)}
              border={`0.5px solid ${activityStatusObj.color}`}
            >
              <MiIcon
                size={0.6}
                path={mdi[activityStatusObj.icon]}
                color={activityStatusObj.color}
                data-testid="status-icon"
              />
              <Typography
                color={activityStatusObj.color}
                variant={TYPOGRAPHY_VARIANTS.CAPTION}
                data-testid="status-text"
                lineHeight={0}
                fontWeight={theme.fontWeight.bold}
              >
                {activityStatusObj.label ?? activityStatus}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      {!readOnly && !hideAction && (
        <MiButton
          startIcon={{
            path: isOnBoard
              ? mdi[ICONS_WEB.MINUS_CIRCLE]
              : mdi[ICONS_WEB.PLUS_CIRCLE],
            iconSize: 0.8,
            iconColor: isOnBoard
              ? theme.palette.red.main
              : theme.palette.green.main,
          }}
          className={classes.iconButton}
          data-testid={isOnBoard ? "remove-btn" : "add-btn"}
          onClick={onAction}
        />
      )}
    </Box>
  );
};

const TechManagementSection = ({
  usersNotOnBoard,
  boardUsers,
  selectedBoardId,
  debouncedSearch,
  handleSave,
  isViewAll,
  selectedUserId,
  setSelectedUserId,
}) => {
  const { classes, theme } = useStyles();
  const { hasPermission } = usePermissions();

  const [usersNotOnBoardCopy, setUsersNotOnBoardCopy] = useState([]);
  const [boardUsersCopy, setBoardUsersCopy] = useState([]);
  const [isModified, setIsModified] = useState();

  const sensors = useSensors(useSensor(PointerSensor));

  const reset = () => {
    setUsersNotOnBoardCopy(usersNotOnBoard);
    setBoardUsersCopy(boardUsers);
    setIsModified(false);
  };

  useEffect(() => {
    reset();
  }, [selectedBoardId]);

  useEffect(() => {
    setIsModified(
      !deepEqual(
        boardUsers.map(({ id }) => id),
        boardUsersCopy.map(({ id }) => id)
      )
    );
  }, [boardUsers, boardUsersCopy]);

  useEffect(() => {
    if (
      deepEqual(
        boardUsers.map(({ id }) => id),
        boardUsersCopy.map(({ id }) => id)
      ) &&
      !deepEqual(boardUsers, boardUsersCopy)
    ) {
      setBoardUsersCopy(boardUsers);
    }
  }, [boardUsers]);

  useEffect(() => {
    if (
      deepEqual(
        usersNotOnBoard.map(({ id }) => id),
        usersNotOnBoardCopy.map(({ id }) => id)
      ) &&
      !deepEqual(usersNotOnBoard, usersNotOnBoardCopy)
    ) {
      setUsersNotOnBoardCopy(usersNotOnBoard);
    }
  }, [usersNotOnBoard]);

  const filterBySearch = (users) =>
    users.filter((user) =>
      user.name.toLowerCase().includes(debouncedSearch.toLowerCase())
    );

  const boardUsersSearched = filterBySearch(boardUsersCopy);
  const usersNotOnBoardSearched = filterBySearch(usersNotOnBoardCopy);

  const onRemoveFromBoard = (user) => (e) => {
    e.stopPropagation();
    setBoardUsersCopy((prev) => prev.filter(({ id }) => id !== user.id));
    setUsersNotOnBoardCopy((prev) => [...prev, user]);
  };

  const onAddToBoard = (user) => () => {
    setUsersNotOnBoardCopy((prev) => prev.filter(({ id }) => id !== user.id));
    setBoardUsersCopy((prev) => [...prev, user]);
  };

  const onSave = () => {
    handleSave(
      selectedBoardId,
      boardUsersCopy.map(({ id }, i) => ({
        id,
        displayOrder: i + 1,
      }))
    );
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over?.id && active.id && active?.id !== over?.id) {
      setBoardUsersCopy((prev) =>
        arrayMove(
          prev,
          prev.findIndex(({ id }) => id === active.id),
          prev.findIndex(({ id }) => id === over.id)
        )
      );
    }
  };

  const handleSelect = (id) => () =>
    setSelectedUserId((prev) => (prev === id ? "" : id));

  return (
    <Box>
      {isModified && (
        <Box className={classes.isModifiedContainer}>
          <MiButton
            variant={BUTTON_VARIANTS.CONTAINED}
            size="small"
            title="Save Technician View"
            width="72%"
            data-testid="save-btn"
            onClick={onSave}
          />
          <MiButton
            variant={BUTTON_VARIANTS.OUTLINED}
            size="small"
            title="Cancel"
            description="Remove Changes"
            data-testid="reset-btn"
            onClick={reset}
          />
        </Box>
      )}
      <Box>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.BODY2}
          className={classes.boardTitle}
        >
          Technicians: On Board
        </Typography>
        <Box>
          {isEmpty(boardUsersSearched) ? (
            <Box className={classes.centerBox}>
              <MiIcon
                path={mdi[ICONS_WEB.ALERT]}
                color={theme.palette.orange.main}
              />
              <Typography variant={TYPOGRAPHY_VARIANTS.CAPTION}>
                No Users Found
              </Typography>
            </Box>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToParentElement]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={boardUsersSearched.map(({ id }) => id)}
                strategy={verticalListSortingStrategy}
              >
                {boardUsersSearched?.map((user, i) => (
                  <UserCard
                    key={user.id}
                    user={user}
                    hideAction={
                      !hasPermission(PERMISSIONS.CALENDAR_BOARDS.UPDATE)
                    }
                    isSelected={selectedUserId === user.id}
                    onSelect={handleSelect(user.id)}
                    readOnly={isViewAll}
                    onAction={onRemoveFromBoard(user)}
                    displayOrder={i + 1}
                    isOnBoard
                    isDraggable
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </Box>
      </Box>
      {!isViewAll && (
        <Box>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.BODY2}
            className={classes.boardTitle}
          >
            Technicians: Not On Board
          </Typography>
          <Box>
            {usersNotOnBoardSearched?.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onAction={onAddToBoard(user)}
                hideAction={!hasPermission(PERMISSIONS.CALENDAR_BOARDS.UPDATE)}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const TechnicianManagementDrawer = memo(
  ({
    open,
    handleClose,
    users = [],
    handleBoardModalOpen,
    updateUsersOnBoard,
    selectedUserId,
    setSelectedUserId,
    selectedBoard = {},
    boardUsers = [],
  }) => {
    const { hasPermission } = usePermissions();

    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce(search);

    const { classes, theme } = useStyles();

    const isViewAll = selectedBoard.showAllUsers;
    const actions = useMemo(() => {
      const currentActions = [];
      if (hasPermission(PERMISSIONS.CALENDAR_BOARDS.UPDATE)) {
        currentActions.push({
          name: "Edit",
          description: "Rename Board",
          icon: mdi[ICONS_WEB.PENCIL],
          onClick: () => handleBoardModalOpen(BOARD_MODAL_MODE.UPDATE),
          disabled: isViewAll,
          className: classes.actionIconButton,
        });
      }

      if (hasPermission(PERMISSIONS.CALENDAR_BOARDS.DELETE)) {
        currentActions.push({
          name: "Delete",
          description: "Delete Board",
          icon: mdi[ICONS_WEB.DELETE],
          onClick: () => handleBoardModalOpen(BOARD_MODAL_MODE.DELETE),
          disabled: isViewAll,
          className: classes.actionIconButton,
        });
      }

      currentActions.push({
        name: "Close",
        description: "Exit Technician Overview",
        icon: mdi[ICONS_WEB.CLOSE],
        onClick: handleClose,
        className: classes.actionIconButton,
      });

      return currentActions;
    }, [isViewAll]);

    useEffect(() => {
      if (open) {
        setSearch("");
      }
    }, [open, selectedBoard]);

    const usersNotOnBoard = users.filter(
      (user) => !boardUsers.some((boardUser) => boardUser.id === user.id)
    );

    return (
      <MiInternalDrawer open={open} className={classes.drawer} width={255}>
        <Box className={classes.container}>
          <Box
            position="sticky"
            top={0}
            zIndex={theme.zIndex.drawer}
            backgroundColor="background.paper"
          >
            <MiSectionHeader
              removeMargin
              variant={SECTION_HEADER_VARIANTS.TEXT}
              title="Overview"
              subtitle={selectedBoard?.name}
              rightAction={
                <MiActions type={ACTIONS_LIST_TYPE.ICONS} actions={actions} />
              }
            />
            <SearchTechnician search={search} setSearch={setSearch} />
          </Box>
          <TechManagementSection
            usersNotOnBoard={usersNotOnBoard}
            boardUsers={boardUsers}
            selectedUserId={selectedUserId}
            setSelectedUserId={setSelectedUserId}
            selectedBoardId={selectedBoard.id}
            handleSave={updateUsersOnBoard}
            debouncedSearch={debouncedSearch}
            isViewAll={isViewAll}
          />
        </Box>
      </MiInternalDrawer>
    );
  }
);

const useStyles = makeStyles()(
  (
    theme,
    { transform, transition, isDragging, isSelected, isOnBoard } = {}
  ) => ({
    drawer: {
      borderRight: `1px solid ${theme.palette.grey.accent}`,
    },
    actionIconButton: {
      padding: theme.spacing(1),
      color: theme.palette.grey.main,
      "&:hover": {
        color: theme.palette.primary.main,
      },
    },
    container: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
    },
    searchContainer: {
      padding: theme.spacing(0, 1),
      margin: theme.spacing(1, 0),
    },
    searchInput: {
      padding: theme.spacing(0, 1),
      margin: 0,
      height: 32,
      fontSize: theme.fontSize.medium,
    },
    isModifiedContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      padding: theme.spacing(0, 1, 1, 1),
    },
    userCardContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      padding: theme.spacing(1),
      borderBottom: `1px solid ${theme.palette.grey.accent}`,
      transform: CSS.Transform.toString(transform),
      opacity: isDragging ? 0.5 : 1,
      cursor: isOnBoard ? "pointer" : "default",
      backgroundColor: isOnBoard
        ? theme.palette.orange[isSelected ? "mediumLight" : "light"]
        : theme.palette.common.white,
      transition,
    },
    userCardNameContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
    userCardContentContainer: {
      display: "flex",
      flexDirection: "column",
      marginLeft: theme.spacing(0.5),
    },
    statusContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      width: "fit-content",
      padding: theme.spacing(0.5),
      gap: theme.spacing(0.5),
    },
    boardTitle: {
      width: "100%",
      fontWeight: theme.fontWeight.medium,
      padding: theme.spacing(1),
      backgroundColor: theme.palette.grey.mediumLight,
      borderBottom: `1px solid ${theme.palette.grey.accent}`,
    },
    avatar: {
      width: 24,
      height: 24,
      borderRadius: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      fontSize: theme.fontSize.small,
    },
    dragHandle: {
      cursor: "grab",
      display: "flex",
      alignItems: "center",
      "&:hover": {
        color: theme.palette.primary.main,
      },
    },
    centerBox: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
    iconButton: {
      padding: theme.spacing(1),
    },
  })
);
